import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Box, Typography, Chip, Button, Avatar, Divider } from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';

const AssetCard = ({ asset }) => {
  const navigate = useNavigate();
  const getRiskBgColor = (level) => {
    switch (level) {
      case 'Critical':
        return '#FEE2E2';
      case 'Medium':
        return '#FEF3C7';
      case 'Low':
        return '#DCFCE7';
      default:
        return '#F3F4F6';
    }
  };

  return (
    <Card
      sx={{ height: '100%', cursor: 'pointer', '&:hover': { borderColor: '#D1D5DB' } }}
      onClick={() => navigate(`/asset/${asset.id}`)}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {asset.image ? (
              <Avatar
                src={asset.image}
                alt={asset.name}
                sx={{ width: 44, height: 44 }}
              />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: '#F1F5F9',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PrecisionManufacturingIcon sx={{ fontSize: 24, color: '#64748B' }} />
              </Box>
            )}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                {asset.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {asset.issue}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={asset.status}
            size="small"
            sx={{
              bgcolor: '#DCFCE7',
              color: '#2E7628',
              border: '1px solid #2E7628',
              fontWeight: 600,
              fontSize: '0.6875rem',
              height: 24,
            }}
          />
        </Box>

        {/* Metrics */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.6875rem', mb: 0.5 }}>
              Asset Risk
            </Typography>
            <Chip
              label={asset.riskLevel}
              size="small"
              sx={{
                bgcolor: getRiskBgColor(asset.riskLevel),
                color: asset.riskColor,
                border: `1px solid ${asset.riskColor}`,
                fontWeight: 600,
                fontSize: '0.6875rem',
                height: 22,
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.6875rem', mb: 0.5 }}>
              RUL
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: asset.rulColor }}>
              {asset.rul.value}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.6875rem', mb: 0.5 }}>
              Failures - Last month
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
              {asset.failuresLastMonth}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ bgcolor: '#E5E7EB', height: 1, borderRadius: 1, mb: 2 }} />


        {/* Last Checked */}
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontSize: '0.6875rem', mb: 2 }}
        >
          Last checked: {asset.lastChecked}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<MonitorHeartOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              borderColor: '#E5E7EB',
              color: 'text.primary',
              fontSize: '0.75rem',
              fontWeight: 500,
              py: 0.75,
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
            }}
          >
            Current Health Overview
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BiotechOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              borderColor: '#E5E7EB',
              color: 'text.primary',
              fontSize: '0.75rem',
              fontWeight: 500,
              py: 0.75,
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
            }}
          >
            Initiate Diagnostics
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssetCard;
